import React, { useState, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useFocusTrap } from "../../../hooks/useFocusTrap";
import {
  addManualArticle,
  getSourceRecentArticles,
  checkArticleUrlExists,
  AddManualArticleRequest,
  ManualArticleResponse,
  SourceRecentArticle,
} from "../../../services/grantsApi";
import { validateArticleUrl } from "../../../utils/url-validator";
import {
  sanitizeErrorMessage,
  extractErrorCode,
  extractExistingArticleId,
} from "../../../utils/error-sanitizer";
import { formatDate } from "../../../utils/date-format";
import { useToast } from "../../../contexts/ToastContext";

interface InformationSource {
  id: string;
  name: string;
  url: string | null;
  sourceType: string;
  status: string;
  lastCheckedAt: string | null;
  project: {
    id: string;
    name: string;
  } | null;
}

interface AddManualArticleModalProps {
  isOpen: boolean;
  onClose: () => void;
  source: InformationSource | null;
}

type ModalState = "form" | "success" | "duplicate";

/** Sources of this type cannot be fetched automatically (Cloudflare/CAPTCHA/geo-block) —
 * their whole reason for existing as MANUAL is that the URL is unreachable by the scraper. */
const MANUAL_SOURCE_TYPE = "MANUAL" as const;

/**
 * Modal for manually adding an article to any source (not just MANUAL ones) —
 * automated feeds do miss articles they should have listed.
 *
 * Leaving the content field empty is the normal case: the backend then fetches the
 * URL itself. Paste content only when the URL is not fetchable (Cloudflare, CAPTCHA) —
 * which, for MANUAL sources specifically, is guaranteed rather than exceptional, so
 * content is required client-side for that source type.
 * Either way the article goes straight into the analysis pipeline.
 *
 * @see docs/specifications/2026-07-14-reczne-dodanie-artykulu-do-dowolnego-zrodla/spec.md
 */
const AddManualArticleModal: React.FC<AddManualArticleModalProps> = ({
  isOpen,
  onClose,
  source,
}) => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const firstInputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Focus trap for accessibility
  useFocusTrap(modalRef, isOpen);

  const [modalState, setModalState] = useState<ModalState>("form");
  const [formData, setFormData] = useState<AddManualArticleRequest>({
    title: "",
    url: "",
    content: "",
  });
  const [urlError, setUrlError] = useState<string | null>(null);
  const [urlWarning, setUrlWarning] = useState<string | null>(null);
  const [contentError, setContentError] = useState<string | null>(null);
  const [isCheckingUrl, setIsCheckingUrl] = useState(false);
  const [successData, setSuccessData] = useState<ManualArticleResponse | null>(null);
  const [duplicateArticleId, setDuplicateArticleId] = useState<string | null>(null);

  // Fetch recent articles for context
  const { data: recentArticles } = useQuery<SourceRecentArticle[]>({
    queryKey: ["source-recent-articles", source?.id],
    queryFn: () => getSourceRecentArticles(source!.id, 5),
    enabled: !!source?.id && isOpen,
  });

  // Add article mutation
  const addMutation = useMutation({
    mutationFn: (data: AddManualArticleRequest) =>
      addManualArticle(source!.id, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["grants-sources"] });
      setSuccessData(response);
      setModalState("success");
    },
    onError: (error: any) => {
      const errorCode = extractErrorCode(error);

      if (errorCode === "INVALID_URL") {
        // Show inline error - preserve form data
        setUrlError(sanitizeErrorMessage(error));
      } else if (errorCode === "ARTICLE_ALREADY_LINKED") {
        // Show duplicate modal
        const existingId = extractExistingArticleId(error);
        setDuplicateArticleId(existingId || null);
        setModalState("duplicate");
      } else {
        // Show toast for other errors
        addToast({
          message: sanitizeErrorMessage(error),
          type: "error",
          duration: 5000,
        });
      }
    },
  });

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({ title: "", url: "", content: "" });
      setUrlError(null);
      setUrlWarning(null);
      setContentError(null);
      setIsCheckingUrl(false);
      setModalState("form");
      setSuccessData(null);
      setDuplicateArticleId(null);
    }
  }, [isOpen]);

  // Focus first input when modal opens
  useEffect(() => {
    if (isOpen && modalState === "form") {
      setTimeout(() => firstInputRef.current?.focus(), 100);
    }
  }, [isOpen, modalState]);

  // Handle ESC key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !addMutation.isPending) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose, addMutation.isPending]);

  // Validate URL on blur and check uniqueness
  const handleUrlBlur = async () => {
    if (!formData.url.trim()) {
      setUrlError(null);
      setUrlWarning(null);
      return;
    }

    // First validate locally (format + SSRF)
    const result = validateArticleUrl(formData.url);
    if (!result.isValid) {
      setUrlError(result.error || null);
      setUrlWarning(null);
      return;
    }

    setUrlError(null);

    // Then check uniqueness with backend
    if (!source?.id) return;

    setIsCheckingUrl(true);
    try {
      const exists = await checkArticleUrlExists(source.id, formData.url);
      if (exists.duplicate) {
        setUrlWarning(
          `Artykuł o tym URL już istnieje${exists.createdAt ? ` (dodany ${formatDate(exists.createdAt)})` : ""}`
        );
      } else {
        setUrlWarning(null);
      }
    } catch {
      // Silently fail - backend will catch duplicates on submit
      setUrlWarning(null);
    } finally {
      setIsCheckingUrl(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate before submit
    const urlValidation = validateArticleUrl(formData.url);
    if (!urlValidation.isValid) {
      setUrlError(urlValidation.error || null);
      return;
    }

    if (!formData.title.trim()) {
      return;
    }

    // MANUAL sources exist precisely because their URL can't be fetched
    // automatically — an empty content field would leave the article stuck
    // in DISCOVERED forever waiting on a fetch that will always fail.
    if (source?.sourceType === MANUAL_SOURCE_TYPE && !formData.content?.trim()) {
      setContentError(
        "Treść jest wymagana — tego źródła nie da się pobrać automatycznie."
      );
      return;
    }

    addMutation.mutate(formData);
  };

  const handleAddAnother = () => {
    setFormData({ title: "", url: "", content: "" });
    setUrlError(null);
    setUrlWarning(null);
    setContentError(null);
    setModalState("form");
    setSuccessData(null);
    addMutation.reset();
    setTimeout(() => firstInputRef.current?.focus(), 100);
  };

  const handleGoToSource = () => {
    onClose();
  };

  if (!isOpen || !source) return null;

  const isLoading = addMutation.isPending;
  const isManualSource = source.sourceType === MANUAL_SOURCE_TYPE;
  const isFormValid =
    formData.title.trim() &&
    formData.url.trim() &&
    !urlError &&
    (!isManualSource || formData.content?.trim());

  // Render success state
  if (modalState === "success" && successData) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
        role="dialog"
        aria-modal="true"
        aria-labelledby="success-title"
      >
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
          <div className="px-6 py-4 border-b border-neutral-200">
            <h2 id="success-title" className="text-lg font-medium text-neutral-900">
              Artykuł został dodany pomyślnie
            </h2>
          </div>

          <div className="px-6 py-4 space-y-4">
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-green-800">{successData.title}</p>
                <p className="text-sm text-green-600">
                  {successData.isNewArticle ? "Nowy artykuł utworzony" : "Artykuł podłączony do źródła"}
                </p>
              </div>
            </div>

            <p className="text-sm text-neutral-600">
              {successData.status === "CONTENT_FETCHED" ? (
                <>
                  Status:{" "}
                  <span className="font-medium">
                    Treść zapisana — artykuł jest w kolejce do analizy
                  </span>
                </>
              ) : (
                <>
                  Status:{" "}
                  <span className="font-medium">
                    Treść zostanie pobrana z podanego URL, następnie artykuł trafi do analizy
                  </span>
                </>
              )}
            </p>
          </div>

          <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-3">
            <button
              type="button"
              onClick={handleAddAnother}
              className="px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-md hover:bg-primary-100"
            >
              Dodaj kolejny
            </button>
            <button
              type="button"
              onClick={handleGoToSource}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
            >
              Powrót do źródła
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render duplicate error state
  if (modalState === "duplicate") {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
        role="dialog"
        aria-modal="true"
        aria-labelledby="duplicate-title"
      >
        <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
          <div className="px-6 py-4 border-b border-neutral-200">
            <h2 id="duplicate-title" className="text-lg font-medium text-neutral-900">
              Artykuł już istnieje
            </h2>
          </div>

          <div className="px-6 py-4 space-y-4">
            <div className="flex items-center gap-3 p-4 bg-yellow-50 rounded-lg">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <p className="text-yellow-800">
                  Artykuł o tym URL już istnieje w tym źródle.
                </p>
              </div>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-neutral-200 flex justify-end gap-3">
            {duplicateArticleId && (
              <a
                href={`/grants/discovery-articles?id=${duplicateArticleId}`}
                className="px-4 py-2 text-sm font-medium text-primary-600 bg-primary-50 rounded-md hover:bg-primary-100"
              >
                Zobacz istniejący
              </a>
            )}
            <button
              type="button"
              onClick={() => {
                setModalState("form");
                setDuplicateArticleId(null);
                addMutation.reset();
              }}
              className="px-4 py-2 text-sm font-medium text-neutral-700 bg-neutral-100 rounded-md hover:bg-neutral-200"
            >
              Zamknij
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Render form
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 my-8">
        <div className="px-6 py-4 border-b border-neutral-200">
          <h2 id="modal-title" className="text-lg font-medium text-neutral-900">
            Dodaj artykuł ręcznie
          </h2>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Source info */}
          <div className="p-4 bg-neutral-50 rounded-lg">
            <p className="text-sm font-medium text-neutral-900">
              Źródło: {source.name}
            </p>
            {source.url && (
              <p className="text-sm text-neutral-600 mt-1">
                URL źródła:{" "}
                <a
                  href={source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:underline"
                >
                  {source.url}
                </a>
              </p>
            )}
            {source.lastCheckedAt && (
              <p className="text-sm text-neutral-500 mt-1">
                Ostatnie sprawdzenie: {formatDate(source.lastCheckedAt)}
              </p>
            )}
          </div>

          {/* Recent articles */}
          {recentArticles && (
            <div className="p-4 bg-neutral-50 rounded-lg">
              <p className="text-sm font-medium text-neutral-700 mb-2">
                Ostatnie artykuły z tego źródła:
              </p>
              {recentArticles.length === 0 ? (
                <p className="text-sm text-neutral-500 italic">Brak artykułów</p>
              ) : (
                <ul className="space-y-1">
                  {recentArticles.map((article) => (
                    <li key={article.id} className="text-sm text-neutral-600">
                      <span className="text-neutral-400 mr-1">•</span>
                      {article.title}
                      <span className="text-neutral-400 ml-1">
                        ({formatDate(article.createdAt)})
                      </span>
                      {article.addedByUserName && (
                        <span className="text-neutral-400 ml-1">
                          - {article.addedByUserName}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Title */}
            <div>
              <label
                htmlFor="article-title"
                className="block text-sm font-medium text-neutral-700 mb-1"
              >
                Tytuł artykułu <span className="text-red-600">*</span>
              </label>
              <input
                ref={firstInputRef}
                id="article-title"
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                className="w-full px-3 py-2 border border-neutral-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Nabor XI - wyniki oceny formalnej"
                required
                disabled={isLoading}
              />
            </div>

            {/* URL */}
            <div>
              <label
                htmlFor="article-url"
                className="block text-sm font-medium text-neutral-700 mb-1"
              >
                URL artykułu <span className="text-red-600">*</span>
              </label>
              <div className="relative">
                <input
                  id="article-url"
                  type="url"
                  value={formData.url}
                  onChange={(e) => {
                    setFormData({ ...formData, url: e.target.value });
                    setUrlError(null);
                    setUrlWarning(null);
                  }}
                  onBlur={handleUrlBlur}
                  className={`w-full px-3 py-2 pr-10 border rounded-md focus:ring-2 focus:ring-primary-500 ${
                    urlError
                      ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                      : urlWarning
                      ? "border-yellow-500 focus:border-yellow-500 focus:ring-yellow-500"
                      : "border-neutral-300 focus:border-primary-500"
                  }`}
                  placeholder="https://example.com/artykul"
                  required
                  disabled={isLoading}
                  aria-invalid={!!urlError}
                  aria-describedby={urlError ? "url-error" : urlWarning ? "url-warning" : "url-hint"}
                />
                {isCheckingUrl && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600" />
                  </div>
                )}
              </div>
              {urlError && (
                <p id="url-error" className="mt-1 text-xs text-red-600" role="alert">
                  {urlError}
                </p>
              )}
              {urlWarning && !urlError && (
                <p id="url-warning" className="mt-1 text-xs text-yellow-600" role="status">
                  ⚠️ {urlWarning}
                </p>
              )}
              {!urlError && !urlWarning && !isCheckingUrl && (
                <p id="url-hint" className="mt-1 text-xs text-neutral-500">
                  URL musi być publiczny (bez localhost, prywatnych IP)
                </p>
              )}
              {isCheckingUrl && (
                <p className="mt-1 text-xs text-neutral-500">
                  Sprawdzanie URL...
                </p>
              )}
            </div>

            {/* Content (optional for automatically fetchable sources, required for MANUAL) */}
            <div>
              <label
                htmlFor="article-content"
                className="block text-sm font-medium text-neutral-700 mb-1"
              >
                Treść artykułu{" "}
                {isManualSource ? (
                  <span className="text-red-600">*</span>
                ) : (
                  "(opcjonalne)"
                )}
              </label>
              <textarea
                id="article-content"
                value={formData.content}
                onChange={(e) => {
                  setFormData({ ...formData, content: e.target.value });
                  if (contentError) setContentError(null);
                }}
                rows={6}
                className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                  contentError ? "border-red-500" : "border-neutral-300"
                }`}
                placeholder="<p>Wyniki oceny formalnej dla naboru XI...</p>"
                disabled={isLoading}
                required={isManualSource}
                aria-required={isManualSource}
                aria-invalid={!!contentError}
                aria-describedby={contentError ? "content-error" : "content-hint"}
              />
              {contentError ? (
                <p id="content-error" className="mt-1 text-xs text-red-600" role="alert">
                  {contentError}
                </p>
              ) : (
                <p id="content-hint" className="mt-1 text-xs text-neutral-500">
                  {isManualSource
                    ? "Wklej treść artykułu — tego źródła nie da się pobrać automatycznie."
                    : "Zostaw puste — treść zostanie pobrana z podanego URL. Wypełnij tylko gdy strony nie da się pobrać (Cloudflare, CAPTCHA)."}{" "}
                  Dozwolone tagi: p, br, strong, em, ul, ol, li, a, h1-h6
                </p>
              )}
            </div>

            <p className="text-xs text-neutral-500">
              Dodany artykuł zostanie automatycznie przeanalizowany przez system.
            </p>
          </form>
        </div>

        <div className="px-6 py-4 border-t border-neutral-200 flex flex-col sm:flex-row justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium text-neutral-700 bg-neutral-100 rounded-md hover:bg-neutral-200 disabled:opacity-50"
          >
            Anuluj
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || !isFormValid}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isLoading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            )}
            {isLoading ? "Dodawanie..." : "Dodaj"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddManualArticleModal;
